import React, { useState, useEffect, useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader } from "../../ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  DotsThree,
  MagnifyingGlass,
  PlusCircle,
  CalendarBlank,
  Buildings,
  MapPin,
  LockLaminatedOpen,
  LockLaminated,
  CaretLeft,
  CaretRight,
  Funnel,
  Check,
  Hourglass,
} from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { parse, parseISO, format, isValid } from "date-fns";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import {
  getVacancies,
  useSendNotificationsToApplicants,
} from "../../../adapters/Requests";
import { useFlashMessageContext } from "../../../contexts/FlashMessageContext";
import {
  Dialog as DialogX,
  DialogContent as DialogContentX,
  DialogHeader,
  DialogTitle as DialogTitleX,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";
import { toast } from "sonner";
import ExportDropdown from "../../export";

const DISPLAY_DATE_FORMAT = "dd MMM yyyy";

function getNullableNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function getVacancyType(vacancy) {
  return String(
    vacancy?.vacancy_position?.name || vacancy?.position || ""
  ).toLowerCase();
}

function getVacancyCourseBreakdown(vacancy) {
  if (!vacancy) return [];

  const detailedCourses = Array.isArray(vacancy.vacancyCourses)
    ? vacancy.vacancyCourses
        .map((entry) => {
          const quota = getNullableNumber(entry?.quota);
          const acceptedValue = getNullableNumber(entry?.accepted);
          const accepted = quota === null ? acceptedValue : acceptedValue ?? 0;

          return {
            id: entry?.id || entry?.courseId,
            name:
              entry?.course?.name ||
              entry?.course?.course ||
              entry?.name ||
              `Course ${entry?.courseId}`,
            quota,
            accepted,
            remaining:
              quota === null ? null : Math.max(quota - (accepted ?? 0), 0),
          };
        })
        .filter((entry) => entry.name)
    : [];

  if (detailedCourses.length > 0) {
    return detailedCourses.sort((a, b) => a.name.localeCompare(b.name));
  }

  const fallbackCourses = Array.isArray(vacancy.courseLists)
    ? vacancy.courseLists
        .map((entry) => ({
          id: entry?.id || entry?.courseId,
          name: entry?.course || entry?.name || `Course ${entry?.courseId}`,
          quota: null,
          accepted: null,
          remaining: null,
        }))
        .filter((entry) => entry.name)
    : [];

  return fallbackCourses.sort((a, b) => a.name.localeCompare(b.name));
}

function normalizeQuotaInput(value) {
  if (value === null || value === undefined) return "";
  return String(value).replace(/[^\d]/g, "");
}

export default function VacancyTable() {
  const apiClientPrivate = useAxiosPrivate();
  const { data, isLoading, isFetching, isError } = getVacancies();

  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [periodFilter, setPeriodFilter] = useState("ALL");

  const [sorting, setSorting] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [uniquePeriods, setUniquePeriods] = useState([]);

  const [editingRowId, setEditingRowId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    id: null,
    loading: false,
  });
  const [openNotificationDialog, setOpenNotificationDialog] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [previewVacancy, setPreviewVacancy] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewLoadError, setPreviewLoadError] = useState("");
  const [selectedVacancyId, setSelectedVacancyId] = useState(undefined);

  const sendNotificationsToApplicants = useSendNotificationsToApplicants();
  const { setOpenFlash, setSeverity, setFlashMessage } =
    useFlashMessageContext();

  const formatDate = (dateValue) => {
    if (!dateValue) return "";

    if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
      return format(dateValue, DISPLAY_DATE_FORMAT);
    }

    if (typeof dateValue === "string") {
      const trimmedDate = dateValue.trim();
      if (!trimmedDate) return "";

      if (trimmedDate.includes("T")) {
        const isoDate = parseISO(trimmedDate);
        if (isValid(isoDate)) {
          return format(isoDate, DISPLAY_DATE_FORMAT);
        }
      }

      const normalizedDate = trimmedDate.split("T")[0];
      const formats = ["MM/dd/yyyy", "dd/MM/yyyy", "yyyy-MM-dd"];
      for (const f of formats) {
        const parsed = parse(normalizedDate, f, new Date());
        if (isValid(parsed)) return format(parsed, DISPLAY_DATE_FORMAT);
      }

      const parsedDate = new Date(trimmedDate);
      if (!Number.isNaN(parsedDate.getTime())) {
        return format(parsedDate, DISPLAY_DATE_FORMAT);
      }

      return trimmedDate;
    }

    const formats = ["MM/dd/yyyy", "dd/MM/yyyy", "yyyy-MM-dd"];
    for (const f of formats) {
      const parsed = parse(String(dateValue), f, new Date());
      if (isValid(parsed)) return format(parsed, DISPLAY_DATE_FORMAT);
    }

    const parsedDate = new Date(dateValue);
    if (!Number.isNaN(parsedDate.getTime())) {
      return format(parsedDate, DISPLAY_DATE_FORMAT);
    }

    return String(dateValue);
  };

  const toDateInputValue = (dateValue) => {
    if (!dateValue) return "";

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    return parsedDate.toISOString().split("T")[0];
  };

  const buildPreviewRow = (vacancy, fallback = {}) => ({
    ...fallback,
    id: vacancy?.id ?? fallback.id,
    code: vacancy?.code ?? fallback.code,
    position:
      vacancy?.vacancy_position?.name?.toUpperCase?.() || fallback.position,
    status: vacancy?.status
      ? String(vacancy.status).toUpperCase()
      : fallback.status,
    location: vacancy?.location?.name ?? fallback.location,
    notifications: vacancy ?? fallback.notifications,
    unit: vacancy?.business_unit?.name ?? fallback.unit,
    description: vacancy?.description ?? fallback.description,
    opening: vacancy?.opening_date
      ? formatDate(vacancy.opening_date)
      : fallback.opening,
    closing: vacancy?.closing_date
      ? formatDate(vacancy.closing_date)
      : fallback.closing,
    rawClosing: vacancy?.closing_date ?? fallback.rawClosing,
    batch: vacancy?.batch?.name ?? fallback.batch,
    quota: vacancy?.quota ?? fallback.quota,
    applicationsCount: Array.isArray(vacancy?.applications)
      ? vacancy.applications.length
      : fallback.applicationsCount,
    original: vacancy ?? fallback.original,
  });

  useEffect(() => {
    if (data) {
      const formattedData = data.map((vacancy) => buildPreviewRow(vacancy));
      setTableData(formattedData);

      const periods = [...new Set(formattedData.map((v) => v.batch))];
      setUniquePeriods(periods);
    }
  }, [data]);

  const filteredData = useMemo(() => {
    return tableData.filter((item) => {
      const matchesSearch =
        globalFilter === "" ||
        Object.values(item).some(
          (val) =>
            typeof val === "string" &&
            val.toLowerCase().includes(globalFilter.toLowerCase())
        );
      const matchesStatus =
        statusFilter === "ALL" || item.status === statusFilter;
      const matchesPeriod =
        periodFilter === "ALL" || item.batch === periodFilter;

      return matchesSearch && matchesStatus && matchesPeriod;
    });
  }, [tableData, globalFilter, statusFilter, periodFilter]);

  const exportColumns = [
    { headerName: "Vacancy ID", field: "code" },
    { headerName: "Position", field: "position" },
    { headerName: "Period", field: "batch" },
    { headerName: "Status", field: "status" },
    { headerName: "Business Unit", field: "unit" },
    { headerName: "Location", field: "location" },
    { headerName: "Opening", field: "opening" },
    { headerName: "Closing", field: "closing" },
    { headerName: "Quota", field: "quota" },
    { headerName: "Applied", field: "applicationsCount" },
  ];

  const handleRowClick = async (e, row) => {
    if (
      e.target.closest("button") ||
      e.target.closest("input") ||
      e.target.closest(".no-click")
    )
      return;

    setPreviewVacancy(row);
    setPreviewLoading(true);
    setPreviewLoadError("");

    try {
      const res = await apiClientPrivate.get(`/vacancy/${row.id}`);
      const detailedRow = buildPreviewRow(res.data, row);

      setPreviewVacancy((current) =>
        current?.id === row.id ? detailedRow : current
      );
      setTableData((prev) =>
        prev.map((item) => (item.id === row.id ? detailedRow : item))
      );
    } catch (error) {
      console.error("Failed to load vacancy preview details:", error);
      setPreviewLoadError(
        "Live vacancy details could not be loaded. Showing available summary."
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleEditClick = (row) => {
    const sourceVacancy = row?.original || row;
    const previewRow = buildPreviewRow(sourceVacancy, row);
    const courseBreakdown = getVacancyCourseBreakdown(sourceVacancy);

    setEditingRowId(previewRow.id);
    setEditForm({
      status: previewRow.status,
      closing: toDateInputValue(previewRow.rawClosing),
      quota: String(previewRow.quota ?? ""),
      originalQuota: Number(previewRow.quota ?? 0),
      siwesQuotas: Object.fromEntries(
        courseBreakdown.map((course) => [course.id, String(course.quota ?? "")]),
      ),
      originalSiwesQuotas: Object.fromEntries(
        courseBreakdown.map((course) => [course.id, Number(course.quota ?? 0)]),
      ),
    });
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    if (isSaving) return;
    setEditDialogOpen(false);
    setEditingRowId(null);
    setEditForm({});
  };

  const handleSaveClick = async () => {
    const row = tableData.find((item) => item.id === editingRowId);
    if (!row) return;

    const vacancyType = getVacancyType(row.original);
    const courseBreakdown = getVacancyCourseBreakdown(row.original);
    const isSiwesVacancy = vacancyType === "siwes" && courseBreakdown.length > 0;

    const normalizedSiwesQuotas = Object.fromEntries(
      courseBreakdown.map((course) => {
        const rawValue = editForm.siwesQuotas?.[course.id];
        const parsedValue = Number(rawValue ?? 0);
        const minimumValue = Number(editForm.originalSiwesQuotas?.[course.id] ?? 0);
        return [
          course.id,
          Number.isFinite(parsedValue)
            ? Math.max(parsedValue, minimumValue)
            : minimumValue,
        ];
      }),
    );

    const nextQuota = isSiwesVacancy
      ? Object.values(normalizedSiwesQuotas).reduce(
          (sum, value) => sum + Number(value || 0),
          0,
        )
      : Math.max(Number(editForm.quota || 0), Number(editForm.originalQuota || 0));

    try {
      setIsSaving(true);
      const res = await apiClientPrivate.put(`/vacancy/update/${row.id}`, {
        status: editForm.status,
        closing_date: editForm.closing,
        quota: nextQuota,
        siwesQuotas: isSiwesVacancy ? normalizedSiwesQuotas : undefined,
      });

      if (res.status === 200) {
        const updatedRow = buildPreviewRow(res.data, row);
        setTableData((prev) =>
          prev.map((item) =>
            item.id === row.id
              ? updatedRow
              : item
          )
        );
        setPreviewVacancy((current) =>
          current?.id === row.id ? updatedRow : current,
        );
        toast.success("Vacancy updated successfully");
        closeEditDialog();
      }
    } catch (e) {
      toast.error("Failed to update vacancy");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteConfirm({ open: true, id, loading: false });
  };

  const confirmDelete = async () => {
    const id = deleteConfirm.id;
    if (!id) return;
    try {
      setDeleteConfirm((prev) => ({ ...prev, loading: true }));
      const res = await apiClientPrivate.put(`/vacancy/delete/${id}`, {
        deleted_at: new Date().toISOString(),
        status: "closed",
      });
      if (res.status === 200) {
        setTableData((prev) => prev.filter((item) => item.id !== id));
        toast.success("Vacancy deleted successfully");
      }
    } catch (e) {
      toast.error("Error deleting vacancy");
    } finally {
      setDeleteConfirm({ open: false, id: null, loading: false });
    }
  };

  const handleSendNotifications = (vacancy) => {
    const closingDate = new Date(vacancy.closing_date);
    const isDateElapsed = closingDate < new Date();
    const isClosed = String(vacancy.status).toUpperCase() === "CLOSED";

    if (!isDateElapsed && !isClosed) {
      setFlashMessage(
        "Emails can only be sent when the vacancy is closed or its closing date has passed."
      );
      setSeverity("error");
      setOpenFlash(true);
      return;
    }
    setSelectedVacancyId(vacancy.id);
    setOpenNotificationDialog(true);
  };

  const sendNotifications = async () => {
    setIsSending(true);
    try {
      const response = await sendNotificationsToApplicants.mutateAsync({
        vacancyId: selectedVacancyId,
      });
      setFlashMessage(response.message);
      setSeverity("success");
    } catch (err) {
      setFlashMessage(err?.response?.data?.message || "Something went wrong!");
      setSeverity("error");
    } finally {
      setOpenFlash(true);
      setOpenNotificationDialog(false);
      setIsSending(false);
      setSelectedVacancyId(undefined);
    }
  };

  const columns = [
    {
      accessorKey: "position",
      header: "Vacancy",
      cell: ({ row }) => (
        <div className="flex flex-col text-center py-1">
          <span className="font-bold text-gray-900 text-sm">
            {row.getValue("position")}
          </span>
          <span className="text-[11px] text-gray-500 font-mono mt-0.5">
            #{row.original.code}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "batch",
      header: "Period",
      cell: ({ row }) => (
        <div className="flex items-center text-center text-gray-600">
          <Hourglass className="mr-2 text-green-600" size={14} />
          <span className="text-sm font-medium">{row.getValue("batch")}</span>
        </div>
      ),
    },
    {
      accessorKey: "unit",
      header: "Business Unit",
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center text-gray-700 text-xs font-semibold">
            <Buildings className="mr-1.5 text-gray-400" size={14} />
            {row.getValue("unit")}
          </div>
          <div className="flex items-center text-gray-500 text-[11px]">
            <MapPin className="mr-1.5 text-gray-400" size={14} />
            {row.original.location}
          </div>
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue("status");
        return (
          <Badge
            variant={status === "OPEN" ? "success" : "secondary"}
            className={`font-semibold text-[10px] px-2 ${status === "CLOSED"
              ? "bg-gray-100 text-gray-500 border-gray-200"
              : ""
              }`}
          >
            {status}
          </Badge>
        );
      },
    },
    {
      accessorKey: "opening",
      header: "Timeline",
      cell: ({ row }) => (
          <div className="flex flex-col gap-0.5 text-xs">
            <span className="text-gray-400">
              Opened: {row.getValue("opening")}
            </span>
            <span className="text-gray-900 font-medium">
              Closes: {row.original.closing}
            </span>
          </div>
      ),
    },
    {
      accessorKey: "quota",
      header: "Stats",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center min-w-[30px]">
            <span className="text-sm font-bold text-gray-900">
              {row.getValue("quota")}
            </span>
            <span className="text-[9px] uppercase text-gray-400 font-bold">
              Quota
            </span>
          </div>
          <div className="h-6 w-[1px] bg-gray-200"></div>
          <div className="flex flex-col items-center min-w-[30px]">
            <span className="text-sm font-bold text-gray-900">
              {row.original.applicationsCount}
            </span>
            <span className="text-[9px] uppercase text-gray-400 font-bold">
              Applied
            </span>
          </div>
        </div>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const hasApps = row.original.applicationsCount > 0;

        return (
          <div className="no-click" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <DotsThree
                    size={24}
                    weight="bold"
                    className="text-gray-400 hover:text-gray-600"
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[180px]">
                <DropdownMenuLabel>Manage</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => handleEditClick(row.original)}>
                  Edit Details
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleSendNotifications(row.original.original)}
                  disabled={row.original.notifications.is_notification_sent}
                >
                  {row.original.notifications.is_notification_sent
                    ? "Notifications Sent"
                    : "Send Decision Mail"}
                </DropdownMenuItem>
                {!hasApps && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleDeleteClick(row.original.id)}
                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                    >
                      Delete Vacancy
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  if (isLoading) {
    return (
      <Card className="w-full h-96 flex items-center justify-center border-slate-200">
        <Loader2 className="w-10 h-10 text-green-600 animate-spin" />
      </Card>
    );
  }

  if (isError) {
    return (
      <div className="p-8 text-center text-red-500">Error loading data.</div>
    );
  }

  return (
    <>
      <Card className="w-full max-w-[95%] m-auto border-slate-200 shadow-sm bg-white">
      <CardHeader className="flex flex-row items-center justify-between p-3 border-b border-slate-100 bg-gray-50/40">
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs border-dashed border-gray-300"
              >
                <Funnel className="mr-2 h-3 w-3" />
                Status: {statusFilter === "ALL" ? "All" : statusFilter}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-40">
              <DropdownMenuItem onClick={() => setStatusFilter("ALL")}>
                {statusFilter === "ALL" && <Check className="mr-2 h-3 w-3" />}{" "}
                All
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter("OPEN")}>
                {statusFilter === "OPEN" && <Check className="mr-2 h-3 w-3" />}{" "}
                Open
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter("CLOSED")}>
                {statusFilter === "CLOSED" && (
                  <Check className="mr-2 h-3 w-3" />
                )}{" "}
                Closed
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs border-dashed border-gray-300"
              >
                <CalendarBlank className="mr-2 h-3 w-3" />
                Period: {periodFilter === "ALL" ? "All" : periodFilter}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-48 max-h-64 overflow-y-auto"
            >
              <DropdownMenuItem onClick={() => setPeriodFilter("ALL")}>
                {periodFilter === "ALL" && <Check className="mr-2 h-3 w-3" />}{" "}
                All Periods
              </DropdownMenuItem>
              {uniquePeriods.map((period) => (
                <DropdownMenuItem
                  key={period}
                  onClick={() => setPeriodFilter(period)}
                >
                  {periodFilter === period && (
                    <Check className="mr-2 h-3 w-3" />
                  )}{" "}
                  {period}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative w-full sm:w-64">
            <MagnifyingGlass
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={14}
            />
            <Input
              placeholder="Search vacancies..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-9 h-8 text-xs bg-white border-slate-200 focus:border-green-500 focus:ring-green-500 transition-colors"
            />
          </div>
          <Link to="/managevacancy">
            <Button className="bg-green-600 hover:bg-green-700 text-white h-8 px-3 text-xs shadow-sm">
              <PlusCircle size={16} className="mr-1.5" weight="bold" />
              Create
            </Button>
          </Link>
          <div className="h-8">
            <ExportDropdown
              onExport={() => { }}
              data={filteredData}
              columns={exportColumns}
              className="h-8"
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0 relative">
        {isFetching && !isLoading && (
          <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-green-600 animate-spin" />
          </div>
        )}
        <Table>
          <TableHeader className="bg-gray-50/50">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="hover:bg-transparent border-b border-slate-100"
              >
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="h-10 text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="hover:bg-slate-50/60 transition-colors border-b border-slate-100 cursor-pointer group"
                  onClick={(e) => handleRowClick(e, row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-3">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-32 text-center text-gray-500"
                >
                  No vacancies found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      <div className="flex items-center justify-between p-2 px-4 border-t border-slate-100 bg-gray-50/30">
        <div className="text-[11px] text-gray-500">
          Page {table.getState().pagination.pageIndex + 1} of{" "}
          {table.getPageCount()}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="h-7 w-20 bg-white text-[11px]"
          >
            <CaretLeft className="mr-1" size={12} /> Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="h-7 w-20 bg-white text-[11px]"
          >
            Next <CaretRight className="ml-1" size={12} />
          </Button>
        </div>
      </div>

      <DialogX
        open={deleteConfirm.open}
        onOpenChange={(open) =>
          !open && setDeleteConfirm((prev) => ({ ...prev, open: false }))
        }
      >
        <DialogContentX>
          <DialogHeader>
            <DialogTitleX>Delete Vacancy</DialogTitleX>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the
              vacancy.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setDeleteConfirm((prev) => ({ ...prev, open: false }))
              }
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmDelete}
              disabled={deleteConfirm.loading}
            >
              {deleteConfirm.loading && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}{" "}
              Delete
            </Button>
          </DialogFooter>
        </DialogContentX>
      </DialogX>

      <DialogX
        open={openNotificationDialog}
        onOpenChange={setOpenNotificationDialog}
      >
        <DialogContentX>
          <DialogHeader>
            <DialogTitleX>Send Decision Emails</DialogTitleX>
            <DialogDescription>
              This will send emails to all applicants for this vacancy informing
              them of the decision.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpenNotificationDialog(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={sendNotifications}
              disabled={isSending}
            >
              {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{" "}
              Send Emails
            </Button>
          </DialogFooter>
        </DialogContentX>
      </DialogX>

      <DialogX open={editDialogOpen} onOpenChange={closeEditDialog}>
        <DialogContentX className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitleX>Edit Vacancy Details</DialogTitleX>
            <DialogDescription>
              Update the closing date, status, and quota for this vacancy.
            </DialogDescription>
          </DialogHeader>

          {(() => {
            const activeRow = tableData.find((item) => item.id === editingRowId);
            const courseBreakdown = activeRow
              ? getVacancyCourseBreakdown(activeRow.original)
              : [];
            const isSiwesVacancy =
              activeRow && getVacancyType(activeRow.original) === "siwes";
            const derivedQuota = courseBreakdown.reduce((sum, course) => {
              const parsedValue = Number(editForm.siwesQuotas?.[course.id] ?? 0);
              const minimumValue = Number(
                editForm.originalSiwesQuotas?.[course.id] ?? 0,
              );
              return (
                sum +
                (Number.isFinite(parsedValue)
                  ? Math.max(parsedValue, minimumValue)
                  : minimumValue)
              );
            }, 0);

            return (
              <div className="space-y-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Status
                    </label>
                    <select
                      value={editForm.status || "OPEN"}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                      className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-900 outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-200"
                    >
                      <option value="OPEN">OPEN</option>
                      <option value="CLOSED">CLOSED</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Closing Date
                    </label>
                    <Input
                      type="date"
                      value={editForm.closing || ""}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          closing: e.target.value,
                        }))
                      }
                      className="h-10"
                    />
                  </div>
                </div>

                {isSiwesVacancy && courseBreakdown.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Course Quotas
                        </p>
                        <p className="text-sm text-slate-600">
                          You can increase quota from the current values, but not reduce them.
                        </p>
                      </div>
                      <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-700">
                        <span className="text-[11px] uppercase tracking-wide text-emerald-700/80">
                          Total
                        </span>
                        <span>{derivedQuota}</span>
                      </div>
                    </div>

                    <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                      {courseBreakdown.map((course) => (
                        <div
                          key={`${course.id}-${course.name}`}
                          className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 md:flex-row md:items-center md:justify-between"
                        >
                          <div>
                            <p className="text-sm font-semibold text-slate-900">
                              {course.name}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              Current quota {editForm.originalSiwesQuotas?.[course.id] ?? 0}
                              {" · "}Used {course.accepted ?? 0} slots
                            </p>
                          </div>

                          <div className="w-full md:w-40">
                            <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              Quota
                            </label>
                            <Input
                              type="number"
                              min={editForm.originalSiwesQuotas?.[course.id] ?? 0}
                              value={editForm.siwesQuotas?.[course.id] ?? ""}
                              onChange={(e) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  siwesQuotas: {
                                    ...(prev.siwesQuotas || {}),
                                    [course.id]: normalizeQuotaInput(e.target.value),
                                  },
                                }))
                              }
                              onBlur={(e) => {
                                const minimumValue = Number(
                                  editForm.originalSiwesQuotas?.[course.id] ?? 0,
                                );
                                const typedValue = Number(e.target.value || 0);
                                setEditForm((prev) => ({
                                  ...prev,
                                  siwesQuotas: {
                                    ...(prev.siwesQuotas || {}),
                                    [course.id]: String(
                                      Math.max(typedValue, minimumValue),
                                    ),
                                  },
                                }));
                              }}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            General Quota
                          </label>
                          <p className="text-sm text-slate-600">
                            Minimum allowed: {editForm.originalQuota ?? 0}
                          </p>
                        </div>
                        <div className="w-full sm:w-40">
                          <Input
                            type="number"
                            min={editForm.originalQuota ?? 0}
                            value={editForm.quota || ""}
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                quota: normalizeQuotaInput(e.target.value),
                              }))
                            }
                            onBlur={(e) => {
                              const minimumValue = Number(editForm.originalQuota ?? 0);
                              const typedValue = Number(e.target.value || 0);
                              setEditForm((prev) => ({
                                ...prev,
                                quota: String(Math.max(typedValue, minimumValue)),
                              }));
                            }}
                            className="h-10 bg-white"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          <DialogFooter>
            <Button variant="outline" onClick={closeEditDialog} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={handleSaveClick}
              disabled={isSaving}
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContentX>
      </DialogX>


    </Card>

      {/* Vacancy Preview Panel */}
      {previewVacancy && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black/30 transition-opacity"
            onClick={() => setPreviewVacancy(null)}
          />
          <div className="relative w-full max-w-lg bg-white shadow-2xl border-l border-slate-200 overflow-y-auto animate-in slide-in-from-right duration-300">
            {(() => {
              const courseBreakdown = getVacancyCourseBreakdown(
                previewVacancy.original
              );
              const vacancyType = getVacancyType(previewVacancy.original);
              const isSiwesVacancy = vacancyType === "siwes";
              const hasQuotaBreakdown =
                isSiwesVacancy &&
                courseBreakdown.some((course) => course.quota !== null);
              const courseSectionTitle = hasQuotaBreakdown
                ? "Courses & Breakdown"
                : "Eligible Courses";
              const courseSectionDescription = hasQuotaBreakdown
                ? "Eligible courses with assigned quota, used slots, and remaining capacity."
                : "Eligible courses attached to this vacancy. The main vacancy quota applies across this full list.";

              return (
                <>
            <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{previewVacancy.position}</h2>
                <span className="text-xs text-gray-500 font-mono">#{previewVacancy.code}</span>
              </div>
              <button
                onClick={() => setPreviewVacancy(null)}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status Badge */}
              <div className="flex items-center gap-3">
                <Badge
                  variant={previewVacancy.status === "OPEN" ? "success" : "secondary"}
                  className={`font-semibold text-xs px-3 py-1 ${previewVacancy.status === "CLOSED" ? "bg-gray-100 text-gray-500 border-gray-200" : ""}`}
                >
                  {previewVacancy.status}
                </Badge>
                <span className="text-sm text-gray-500">·</span>
                <span className="text-sm text-gray-600 font-medium">{previewVacancy.batch}</span>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 border border-green-100 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">{previewVacancy.quota}</p>
                  <p className="text-xs text-green-600 font-semibold uppercase mt-1">Quota</p>
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700">{previewVacancy.applicationsCount}</p>
                  <p className="text-xs text-blue-600 font-semibold uppercase mt-1">Applications</p>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-amber-700">{courseBreakdown.length}</p>
                  <p className="text-xs text-amber-600 font-semibold uppercase mt-1">Courses</p>
                </div>
              </div>

              {previewLoading && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                  Loading latest vacancy breakdown...
                </div>
              )}

              {!previewLoading && previewLoadError && (
                <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                  {previewLoadError}
                </div>
              )}

              {/* Details */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-start gap-3">
                    <Buildings
                      size={18}
                      className="mt-0.5 shrink-0 text-gray-400"
                    />
                    <div>
                      <p className="text-xs font-semibold uppercase text-gray-500">
                        Business Unit
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {previewVacancy.unit}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-start gap-3">
                    <MapPin size={18} className="mt-0.5 shrink-0 text-gray-400" />
                    <div>
                      <p className="text-xs font-semibold uppercase text-gray-500">
                        Location
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {previewVacancy.location}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-start gap-3">
                    <CalendarBlank
                      size={18}
                      className="mt-0.5 shrink-0 text-gray-400"
                    />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase text-gray-500">
                        Timeline
                      </p>
                      <p className="text-sm text-gray-900">
                        Opened:{" "}
                        <span className="font-medium">{previewVacancy.opening}</span>
                      </p>
                      <p className="text-sm text-gray-900">
                        Closes:{" "}
                        <span className="font-medium">{previewVacancy.closing}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {courseBreakdown.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs text-gray-500 uppercase font-semibold">
                      {courseSectionTitle}
                    </p>
                    {hasQuotaBreakdown && (
                      <span className="text-[11px] text-gray-500">
                        Per-course quota tracking
                      </span>
                    )}
                  </div>

                  <div className="bg-gray-50 rounded-lg border border-gray-100 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 bg-white/70">
                      <p className="text-sm text-gray-700">
                        {courseSectionDescription}
                      </p>
                    </div>

                    <div className="divide-y divide-gray-100">
                      {courseBreakdown.map((course) => (
                        <div
                          key={`${course.id}-${course.name}`}
                          className="px-4 py-3 flex items-start justify-between gap-4"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 break-words">
                              {course.name}
                            </p>
                            {hasQuotaBreakdown && course.quota !== null ? (
                              <p className="text-xs text-gray-500 mt-1">
                                Breakdown for this course
                              </p>
                            ) : (
                              <p className="text-xs text-gray-500 mt-1">
                                Eligible for this vacancy
                              </p>
                            )}
                          </div>

                          {hasQuotaBreakdown && course.quota !== null ? (
                            <div className="flex flex-wrap justify-end gap-2 shrink-0">
                              <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                                Quota {course.quota}
                              </span>
                              <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-1 text-[11px] font-semibold text-blue-700">
                                Used {course.accepted}
                              </span>
                              <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                                Left {course.remaining}
                              </span>
                            </div>
                          ) : (
                            <span className="inline-flex rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-700 shrink-0">
                              General quota
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {!previewLoading && courseBreakdown.length === 0 && (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {courseSectionTitle}
                  </p>
                  <p className="mt-2 text-sm text-slate-600">
                    No eligible courses have been linked to this vacancy yet.
                  </p>
                </div>
              )}

              {/* Description */}
              {previewVacancy.description && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Description</p>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-gray-50 rounded-lg p-4 border border-gray-100">
                    {previewVacancy.description}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white flex-1"
                  onClick={() => {
                    handleEditClick(previewVacancy);
                    setPreviewVacancy(null);
                  }}
                >
                  Edit Details
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setPreviewVacancy(null)}
                >
                  Close
                </Button>
              </div>
            </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}
